import { filters, FILTER_TYPE, API_INFINITY_VALUE } from '../../../constants';
import { dots } from '../../../functions';
import type { CustomCompanyFilter } from '../filters';

export const generatePastFilters = (): CustomCompanyFilter[] => [
  {
    id: filters.pastFiveYearGrowth,
    label: 'Past 5 Year Earnings Growth',
    labels: {
      ...dots(50, { start: -25, step: 12.5 }),
      '-30': '-∞',
      '-25': '-25%',
      0: '0%',
      25: '25%',
      30: '∞',
    },
    defaultValue: `["${filters.pastFiveYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.pastFiveYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -30;
        } else if (value >= API_INFINITY_VALUE) {
          return 30;
        } else {
          return value;
        }
      }),
    min: -30,
    max: 30,
    type: FILTER_TYPE.RANGE,
    softMin: -25,
    softMax: 25,
    valueResolver: (range) =>
      range.map((value) =>
        value < -25
          ? API_INFINITY_VALUE * -1
          : value > 25
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  {
    id: filters.pastOneYearGrowth,
    label: 'Past 1 Year Earnings Growth',
    labels: {
      ...dots(100, { start: -50, step: 25 }),
      '-55': '-∞',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.pastOneYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.pastOneYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -55;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -55,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -50,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -50
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  {
    id: filters.currentRoe,
    label: 'ROE Ratio',
    subLabel: '(Return on Equity)',
    labels: {
      ...dots(50, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    min: 0,
    max: 55,
    defaultValue: `["${filters.currentRoe}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.currentRoe}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
  },
  {
    id: filters.currentRoa,
    label: 'ROA Ratio',
    subLabel: '(Return on Assets)',
    labels: {
      ...dots(50, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    min: 0,
    max: 55,
    defaultValue: `["${filters.currentRoa}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.currentRoa}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // Special case `onToggle` event
  // Untoggle `is_eps_less_than_zero` if `EPS` is toggled
  {
    id: filters.eps,
    label: 'EPS',
    subLabel: '(Earning per Share)',
    labels: {
      ...dots(100, { step: 10 }),
      0: '0',
      50: '50',
      100: '100',
      105: '∞',
    },
    min: 0,
    max: 105,
    defaultValue: `["${filters.eps}", "between", 50, 100]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.eps}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min} and ${max === API_INFINITY_VALUE ? '∞' : max}`,
  },
  // Special case `onToggle` event that
  // removes the toggle of `EPS` filter
  // and vice versa
  // 😤😤😤
  {
    id: 'is_eps_less_than_zero',
    label: 'Only show loss making companies',
    type: FILTER_TYPE.TOGGLE,
    defaultValue: `["${filters.eps}", "<", 0]`,
    rule: `["${filters.eps}", "<", 0]`,
  },
];
